import { uploadDocumentToR2 } from './r2Storage';

/**
 * List of all database tables included in disaster recovery backup
 */
export const BACKUP_TABLES = [
  'item_master',
  'supplies',
  'instruments',
  'orders',
  'logs',
  'receipts',
  'receipt_items',
  'instrument_tags',
  'sterile_options',
  'user_profiles',
];

/**
 * Creates a full database backup by querying all tables from Supabase
 *
 * @param {object} supabase - Supabase client instance
 * @param {function} [onProgress] - Optional progress callback (table, index, total)
 * @returns {Promise<{ backupData: object, jsonString: string, totalRecords: number, filename: string }>}
 */
export const createDatabaseBackup = async (supabase, onProgress = null) => {
  if (!supabase || typeof supabase.from !== 'function') {
    throw new Error('Supabase client is not available');
  }

  const tables = {};
  const tableCounts = {};
  let totalRecords = 0;

  for (let i = 0; i < BACKUP_TABLES.length; i++) {
    const tableName = BACKUP_TABLES[i];
    if (typeof onProgress === 'function') {
      try {
        onProgress(tableName, i + 1, BACKUP_TABLES.length);
      } catch (e) {
        // Ignore progress callback errors
      }
    }

    const { data, error } = await supabase.from(tableName).select('*');
    if (error) {
      console.warn(`[BackupHelper] Error querying table "${tableName}":`, error);
      throw new Error(`ไม่สามารถดึงข้อมูลตาราง ${tableName}: ${error.message || error}`);
    }

    const rows = Array.isArray(data) ? data : [];
    tables[tableName] = rows;
    tableCounts[tableName] = rows.length;
    totalRecords += rows.length;
  }

  const createdAt = new Date().toISOString();
  const timestamp = createdAt.replace(/[:.]/g, '-');
  const filename = `dental_backup_${timestamp}.json`;

  const backupData = {
    version: '1.0',
    createdAt,
    database: 'supabase',
    app: 'dental-app',
    tableCounts,
    totalRecords,
    tables,
  };

  const jsonString = JSON.stringify(backupData, null, 2);

  return {
    backupData,
    jsonString,
    totalRecords,
    filename,
  };
};

/**
 * Uploads a database backup JSON snapshot to Cloudflare R2
 * and logs the action in the audit logs table.
 *
 * @param {object} backupResult - Result object from createDatabaseBackup
 * @param {object} [supabase] - Optional Supabase client for writing audit log
 * @returns {Promise<{ success: boolean, url: string, filename: string, totalRecords: number }>}
 */
export const uploadBackupToR2 = async (backupResult, supabase = null) => {
  if (!backupResult || !backupResult.jsonString) {
    throw new Error('No backup data provided for upload');
  }

  const filename =
    backupResult.filename ||
    `dental_backup_${new Date().toISOString().replace(/[:.]/g, '-')}.json`;

  // Prepare JSON file or blob for upload
  let fileObj;
  if (typeof File !== 'undefined') {
    try {
      fileObj = new File([backupResult.jsonString], filename, {
        type: 'application/json',
      });
    } catch (e) {
      fileObj = new Blob([backupResult.jsonString], {
        type: 'application/json',
      });
      fileObj.name = filename;
    }
  } else if (typeof Blob !== 'undefined') {
    fileObj = new Blob([backupResult.jsonString], {
      type: 'application/json',
    });
    fileObj.name = filename;
  } else {
    // Fallback in environments without File/Blob
    fileObj = {
      name: filename,
      type: 'application/json',
      content: backupResult.jsonString,
    };
  }

  // Upload to R2 under the 'backups' folder
  const url = await uploadDocumentToR2(fileObj, 'backups');

  // Record audit log if Supabase is connected
  if (supabase && typeof supabase.from === 'function') {
    try {
      await supabase.from('logs').insert([
        {
          item_name: 'Database Backup',
          warehouse: 'Cloudflare R2',
          action: `สำรองข้อมูลฐานข้อมูล Supabase (${backupResult.totalRecords} รายการ) ลง Cloudflare R2 สำเร็จ`,
        },
      ]);
    } catch (logErr) {
      console.warn('[BackupHelper] Audit log insertion failed:', logErr);
    }
  }

  return {
    success: true,
    url,
    filename,
    totalRecords: backupResult.totalRecords,
  };
};

/**
 * Triggers a browser download of the backup snapshot as a .json file
 *
 * @param {object} backupResult - Result object from createDatabaseBackup
 * @returns {{ success: boolean, filename: string }}
 */
export const downloadBackupLocally = (backupResult) => {
  if (!backupResult || !backupResult.jsonString) {
    throw new Error('No backup data to download');
  }

  const filename =
    backupResult.filename ||
    `dental_backup_${new Date().toISOString().replace(/[:.]/g, '-')}.json`;

  const blob = new Blob([backupResult.jsonString], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return {
    success: true,
    filename,
  };
};

/**
 * Retrieves the last backup summary from localStorage
 *
 * @returns {object|null}
 */
export const getLastBackupInfo = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = window.localStorage.getItem('LAST_DB_BACKUP_INFO');
      if (stored) {
        return JSON.parse(stored);
      }
    }
  } catch (e) {
    // Ignore localStorage errors
  }
  return null;
};

/**
 * Stores the last backup summary into localStorage
 *
 * @param {object} info - Backup summary details
 */
export const saveLastBackupInfo = (info) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('LAST_DB_BACKUP_INFO', JSON.stringify(info));
    }
  } catch (e) {
    // Ignore localStorage errors
  }
};
